import '@Components/Charts/AreaChart/AreaChart.scss';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Area,
  AreaChart as AC,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  Line,
  ComposedChart
} from 'recharts';
import * as R from 'ramda';
import { capitalizeWords } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { Areas } from '@Components/Charts/AreaChart/AreaChart';
import { LineDataKeys } from '@Components/Charts/LineChart/LineChart';
import { ChartTooltip, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltip';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { headerFormatter, useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { DefaultLegend } from '@Components/Charts/Legend/DefaultLegend/DefaultLegend';
import { LegendWithGrouping } from '@Components/Charts/Legend/LegendWithGrouping';
import { ConfidenceLegend } from '@Pages/GeoMMM/Results/Components/ConfidenceLegend';
import { getColorMap, Channel7, Neutral500 } from '../../../../utils/colors';
import { useCompanyInfo } from '../../../../utils/hooks/useCompanyInfo';
import {
  buildSortedTooltipItems,
  DateGroupedTooltipContent,
  TooltipPayloadItem,
  TooltipShapeType
} from './tooltip-utils';

// The confidence view always renders three series (lower / band / line); the line
// series — the one surfaced in the tooltip — is the third payload entry.
const CONFIDENCE_LINE_INDEX = 2;

interface LineTooltipContentProps {
  active?: boolean;
  label?: string | number;
  payload?: TooltipPayloadItem[];
  activeArea: string | null;
  colorMap: Record<string, string>;
  dateGrouping?: string;
  tooltipFormatter?: (value: number) => string;
  tooltipShape?: TooltipShapeType;
}

const LineTooltipContent = ({
  active,
  label,
  payload,
  activeArea,
  colorMap,
  dateGrouping,
  tooltipFormatter,
  tooltipShape
}: LineTooltipContentProps) => {
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  if (!active || !payload || !payload[0]) {
    return null;
  }
  const standardFormatter = (val: number) => (val ? val.toFixed(2) : val);
  if (activeArea === null) {
    const formattedHeaderLabel = headerFormatter(dateGrouping, String(label), fiscalCal);
    const items = buildSortedTooltipItems(
      payload,
      item => capitalizeWords(item.name ?? ''),
      value => (tooltipFormatter ? tooltipFormatter(value) : standardFormatter(value))
    );

    return <ChartTooltip headerLabel={formattedHeaderLabel} items={items} shape='line' />;
  }
  if (R.isNil(payload[CONFIDENCE_LINE_INDEX])) {
    return null;
  }
  const item = payload[CONFIDENCE_LINE_INDEX];
  if (R.isNil(item.value) || Number.isNaN(Number(item.value))) {
    return null;
  }
  let lowerValue;
  let upperValue;
  const isBaseline = String(item.name).includes('baseline');

  if (!R.isNil(item.payload[`${item.name}_$lower`]) && !isBaseline) {
    const lowVal = item.payload[`${item.name}_$lower`];
    lowerValue = tooltipFormatter ? tooltipFormatter(lowVal) : lowVal;
  }
  if (!R.isNil(item.payload[`${item.name}_$upper`]) && !isBaseline) {
    const upVal = item.payload[`${item.name}_$upper`];
    upperValue = tooltipFormatter ? tooltipFormatter(upVal) : upVal;
  }

  const formattedHeaderLabel = `${capitalizeWords(String(item.name))} ${headerFormatter(dateGrouping, String(label), fiscalCal)}`;
  const svgIcon = isBaseline ? undefined : (
    <ConfidenceLegend
      showBackground={false}
      color={colorMap[activeArea]}
      backgroundColor={colorMap[activeArea]}
    />
  );
  const items: PayloadItem[] = [
    {
      label: isBaseline ? 'Baseline' : 'Estimate',
      value: tooltipFormatter ? tooltipFormatter(item.value) : item.value,
      color: colorMap[activeArea],
      shape: 'line',
      svgIcon
    }
  ];
  if (!R.isNil(lowerValue) && !R.isNil(upperValue)) {
    items.push({
      label: 'Confidence',
      value: `${lowerValue} – ${upperValue}`,
      color: colorMap[activeArea],
      shape: 'square',
      iconClassName: 'partialOpacity'
    });
  }
  return <ChartTooltip headerLabel={formattedHeaderLabel} items={items} shape={tooltipShape} />;
};

interface OutcomeAndResponseChartProps {
  areas: Areas | LineDataKeys;
  activeArea: string | null;
  setActiveArea: Dispatch<SetStateAction<string | null>>;
  activeGroup: string | null;
  setActiveGroup: Dispatch<SetStateAction<string | null>>;
  data: any[];
  dateGrouping?: string;
  colorOverrides?: Record<string, string>;
  hardCodedColor: string;
  hardCodedColorName: string;
  groupedAreas?: any[];
  tooltipFormatter?: (value: any) => string;
  tooltipShape?: TooltipShapeType;
  reverseToolTipItems?: boolean;
  reverseLegend?: boolean;
  usePercentageYAxis?: boolean;
  xAxisDataKey: string;
  xAxisTickFormatter?: (value: any) => string;
  yAxisTickFormatter?: (value: any) => string;
  legendTitle?: string;
  groupBy: string;
  isResponse?: boolean;
  logScale?: boolean;
  setLogScale?: (value: boolean) => void;
  logScaleThreshold?: number;
}

export const OutcomeAndResponseChart = ({
  areas,
  activeArea,
  setActiveArea,
  activeGroup,
  setActiveGroup,
  data,
  dateGrouping,
  colorOverrides,
  hardCodedColor,
  hardCodedColorName,
  groupedAreas,
  usePercentageYAxis = false,
  xAxisDataKey,
  xAxisTickFormatter,
  yAxisTickFormatter,
  legendTitle,
  tooltipShape,
  reverseToolTipItems,
  reverseLegend,
  tooltipFormatter,
  groupBy,
  isResponse = false,
  logScale = false,
  setLogScale = () => {},
  logScaleThreshold = 1
}: OutcomeAndResponseChartProps) => {
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data
  });

  const activeAreaRef = useRef<string | null>(null);
  const activeGroupRef = useRef<string | null>(null);
  useEffect(() => {
    // Don't reset activeArea/activeGroup if they were set by a legend click
    if (activeAreaRef.current === null) {
      setActiveArea(null);
      setActiveGroup(null);
      setHoveredLegend(null);
    }
  }, [groupBy, isResponse, data, areas, groupedAreas, setActiveArea, setActiveGroup]);

  // Transform data for log scale if needed
  const transformedData = useMemo(() => {
    if (!logScale || !data || R.isEmpty(data)) {
      return data;
    }

    return data.map(item => {
      const transformedItem = { ...item };
      Object.keys(transformedItem).forEach(key => {
        if (key !== xAxisDataKey && typeof transformedItem[key] === 'number') {
          const value = transformedItem[key] || -1;
          // Ignore any values less than 1 for log scale by default
          transformedItem[key] = value < logScaleThreshold ? null : value;
        }
      });
      return transformedItem;
    });
  }, [data, logScale, xAxisDataKey, logScaleThreshold]);

  const colorMap = useMemo<Record<string, string>>(() => {
    if (R.isEmpty(areas)) {
      return {};
    }
    const colorsOriginal = getColorMap(
      areas as { name: string }[],
      hardCodedColorName,
      hardCodedColor
    );

    return { ...colorsOriginal, ...colorOverrides };
  }, [areas, hardCodedColorName, hardCodedColor, colorOverrides]);

  const handleLegendClick = useCallback(
    (line: string, groupChannel?: string) => {
      if (activeArea === line) {
        setActiveArea(null);
        setActiveGroup(null);
        activeAreaRef.current = null;
        activeGroupRef.current = null;
      } else {
        setLogScale(false);
        setActiveArea(line);
        activeAreaRef.current = line;
        if (groupChannel) {
          setActiveGroup(groupChannel);
          activeGroupRef.current = groupChannel;
        }
      }
    },
    [activeArea, setActiveArea, setActiveGroup, setLogScale]
  );

  const handleLegendEnter = useCallback(
    line => {
      setHoveredLegend(hoveredLegend === line ? null : line);
    },
    [hoveredLegend]
  );

  const buildItems = useCallback(
    (chartData, activeLine, hoveredLine, isArea) => {
      const lines = chartData.map((area, index: number) => {
        const isActiveLine = activeLine === null ? true : activeLine === area.name;
        const isConfidenceView = activeLine !== null;
        const isHoveredLegend = hoveredLine === null ? true : hoveredLine === area.name;
        const activeColor = isActiveLine ? colorMap[area.name] : Neutral500;
        const hoverColor = isHoveredLegend ? colorMap[area.name] : Neutral500;
        if (!isActiveLine && activeLine !== null) {
          return null;
        }

        return isArea ? (
          <Area
            activeDot={false}
            dataKey={area.dataKey}
            fill={activeArea ? activeColor : hoverColor}
            fillOpacity={1}
            isAnimationActive={false}
            key={`area-${index}`}
            name={area.name}
            stackId='1'
            stroke={activeArea ? activeColor : hoverColor}
            strokeWidth={0}
            type='monotone'
          />
        ) : (
          <Fragment key={`line-${index}`}>
            {isConfidenceView && (
              <Fragment key={`confidence-${index}`}>
                <Area
                  type='linear'
                  dataKey={`${area.dataKey}_$lower`}
                  // light lavender for the confidence lower bound — no palette equivalent
                  stroke='#EED2FF'
                  fillOpacity={0}
                  strokeWidth={0}
                  dot={false}
                  activeDot={false}
                  stackId='confidence'
                  key={`lower-${index}`}
                />
                <Area
                  type='linear'
                  dataKey={`${area.dataKey}_$band`}
                  fill={activeArea ? activeColor : hoverColor}
                  fillOpacity={0.25}
                  strokeWidth={0}
                  activeDot={false}
                  dot={false}
                  stackId='confidence'
                  key={`band-${index}`}
                />
              </Fragment>
            )}
            <Line
              dataKey={area.dataKey}
              dot={false}
              isAnimationActive={false}
              key={`line-element-${index}`}
              stroke={activeLine ? activeColor : hoverColor}
              strokeWidth={3}
              strokeDasharray={
                isConfidenceView && !String(area.dataKey).includes('baseline') ? '4' : undefined
              }
              type='monotone'
            />
          </Fragment>
        );
      });
      return lines;
    },
    [colorMap, activeArea]
  );

  const chart = useMemo(() => {
    const percentageYAxis = (
      <YAxis
        domain={[0, 100]}
        orientation='left'
        tick={TICK_STYLE}
        ticks={[0, 25, 50, 75, 100]}
        type='number'
        yAxisId='0'
        unit='%'
        width={yAxisWidth}
      />
    );
    const leftYAxis = (
      <YAxis
        axisLine={false}
        domain={['auto', 'auto']}
        orientation='left'
        tick={TICK_STYLE}
        tickFormatter={tick => `${yAxisTickFormatter ? yAxisTickFormatter(tick) : tick}`}
        tickLine={false}
        type='number'
        width={yAxisWidth}
        scale={logScale ? 'log' : 'linear'}
      />
    );
    const xxAxis = (
      <XAxis
        axisLine={false}
        dataKey={xAxisDataKey}
        height={xAxisHeight}
        tick={TICK_STYLE}
        tickFormatter={xAxisTickFormatter}
      />
    );
    const useLeftAxis = true && !usePercentageYAxis;
    const lineTooltip = (
      <Tooltip
        content={
          <LineTooltipContent
            activeArea={activeArea}
            colorMap={colorMap}
            dateGrouping={dateGrouping}
            tooltipFormatter={tooltipFormatter}
            tooltipShape={tooltipShape}
          />
        }
        isAnimationActive={false}
        wrapperStyle={{ zIndex: 20, pointerEvents: 'auto' }}
      />
    );
    return activeArea === null && !(isResponse || logScale) ? (
      <AutoSizer
        key='outcomeAndResponseGeoMMMArea'
        renderProp={({ width, height }) => (
          <AC data={transformedData} height={height} ref={setChartRef} width={width}>
            <CartesianGrid
              stroke={CARTESIAN_GRID_STROKE}
              strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
              vertical={false}
            />
            {xxAxis}
            {usePercentageYAxis && percentageYAxis}
            {!usePercentageYAxis && (
              <YAxis
                axisLine={false}
                tick={TICK_STYLE}
                tickFormatter={yAxisTickFormatter}
                tickLine={false}
                width={yAxisWidth}
              />
            )}
            {buildItems(areas, activeArea, hoveredLegend, true)}
            <Tooltip
              content={
                <DateGroupedTooltipContent
                  dateGrouping={dateGrouping}
                  getLabel={item => item.name ?? ''}
                  formatValue={value => (tooltipFormatter ? tooltipFormatter(value) : value)}
                  shape={tooltipShape}
                  reverseItems={reverseToolTipItems}
                />
              }
              isAnimationActive={false}
              wrapperStyle={{ zIndex: 20, pointerEvents: 'auto' }}
            />
          </AC>
        )}
      />
    ) : (
      <AutoSizer
        key='outcomeAndResponseGeoMMMLine'
        renderProp={({ width, height }) => (
          <ComposedChart data={transformedData} height={height} width={width} ref={setChartRef}>
            <CartesianGrid
              stroke={CARTESIAN_GRID_STROKE}
              strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
              vertical={false}
            />
            {xxAxis}
            {usePercentageYAxis && percentageYAxis}
            {useLeftAxis && leftYAxis}
            {lineTooltip}
            {buildItems(areas, activeArea, hoveredLegend, false)}
          </ComposedChart>
        )}
      />
    );
  }, [
    yAxisWidth,
    logScale,
    xAxisDataKey,
    xAxisHeight,
    xAxisTickFormatter,
    usePercentageYAxis,
    activeArea,
    isResponse,
    yAxisTickFormatter,
    dateGrouping,
    tooltipFormatter,
    colorMap,
    tooltipShape,
    transformedData,
    setChartRef,
    buildItems,
    areas,
    hoveredLegend,
    reverseToolTipItems
  ]);

  return (
    <div className='area-chart' style={{ display: 'flex', flex: 1 }}>
      <div style={{ display: 'flex', flex: 1 }}>{chart}</div>
      <div className='right-of-chart'>
        <div className='legend-title'>{legendTitle}</div>
        {groupedAreas && !R.isEmpty(groupedAreas) ? (
          <LegendWithGrouping
            activeArea={activeArea}
            activeGroup={activeGroup}
            handleLegendClick={handleLegendClick}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            incrementality={false}
            colorMap={colorMap}
            groupedAreas={groupedAreas}
            showDash={logScale || isResponse || activeArea === 'Baseline'}
            svg={
              activeArea === 'Baseline' ? undefined : (
                <ConfidenceLegend
                  showBackground
                  color={activeArea !== null ? colorMap[activeArea] : Channel7}
                  backgroundColor={activeArea !== null ? colorMap[activeArea] : Channel7}
                />
              )
            }
          />
        ) : (
          <DefaultLegend
            areas={areas as Areas}
            activeArea={activeArea}
            handleLegendClick={handleLegendClick}
            incrementality={false}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            colorMap={colorMap}
            reverseLegend={reverseLegend}
            showDash={logScale || isResponse || activeArea === 'Baseline'}
            svg={
              activeArea === 'Baseline' ? undefined : (
                <ConfidenceLegend
                  showBackground
                  color={activeArea !== null ? colorMap[activeArea] : Channel7}
                  backgroundColor={activeArea !== null ? colorMap[activeArea] : Channel7}
                />
              )
            }
          />
        )}
      </div>
    </div>
  );
};
